// Session storage utilities for sales tax filters
import { ProductFilterState, TransactionFilterState, CustomerFilterState, ExemptionCertificateFilterState, CalculationFilterState } from "../filters/types";
import { SortState } from "@commenda/api/new-sales-tax/transactions/types";

type SessionStorageFilterState = ProductFilterState | TransactionFilterState | CustomerFilterState | ExemptionCertificateFilterState | CalculationFilterState;

export const getAllFieldsExceptCorporationId = (filters: SessionStorageFilterState) => {
  return Object.keys(filters).reduce((acc, key) => {
    if (key !== 'corporationId' && filters[key] !== undefined) {
      acc[key] = filters[key];
    }
    return acc;
  }, {});
};

// Generic filter storage
export const getFilterSessionStorageKey = (corporationId: number, tableId: string) => 
  `sales-tax-${tableId}-filters-corporation-${corporationId}`;

export const saveFiltersToSessionStorage = (
  corporationId: number, 
  tableId: string, 
  filters: SessionStorageFilterState
) => {
  if (typeof window === 'undefined') return;
  
  const filtersToSave = getAllFieldsExceptCorporationId(filters);
  
  sessionStorage.setItem(
    getFilterSessionStorageKey(corporationId, tableId),
    JSON.stringify(filtersToSave)
  );
};

export const loadFiltersFromSessionStorage = (corporationId: number, tableId: string): SessionStorageFilterState => {
  if (typeof window === 'undefined') return { corporationId };
  
  try {
    const savedFilters = sessionStorage.getItem(getFilterSessionStorageKey(corporationId, tableId));
    if (savedFilters) {
      return JSON.parse(savedFilters);
    }
  } catch (error) {
    console.warn(`Failed to load ${tableId} filters from session storage:`, error);
  }
  
  return { corporationId };
};

// Sort state session storage
export const getSortStateSessionStorageKey = (corporationId: number, tableId: string) => 
  `sales-tax-${tableId}-sort-state-corporation-${corporationId}`;

export const saveSortStateToSessionStorage = (corporationId: number, tableId: string, sortState: SortState) => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem(
    getSortStateSessionStorageKey(corporationId, tableId),
    JSON.stringify(sortState)
  );
};

export const loadSortStateFromSessionStorage = (corporationId: number, tableId: string): SortState | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const savedSortState = sessionStorage.getItem(getSortStateSessionStorageKey(corporationId, tableId));
    if (savedSortState) {
      return JSON.parse(savedSortState);
    }
  } catch (error) {
    console.warn('Failed to load sort state from session storage:', error);
  }
  
  return null;
};

// Search term session storage
export const getSearchTermSessionStorageKey = (corporationId: number, tableId: string) => 
  `sales-tax-${tableId}-search-term-corporation-${corporationId}`;

export const saveSearchTermToSessionStorage = (corporationId: number, tableId: string, searchTerm: string) => {
  if (typeof window === 'undefined') return;
  
  sessionStorage.setItem(
    getSearchTermSessionStorageKey(corporationId, tableId),
    JSON.stringify(searchTerm)
  );
};

export const loadSearchTermFromSessionStorage = (corporationId: number, tableId: string): string => {
  if (typeof window === 'undefined') return '';
  
  try {
    const savedSearchTerm = sessionStorage.getItem(getSearchTermSessionStorageKey(corporationId, tableId));
    if (savedSearchTerm) {
      return JSON.parse(savedSearchTerm);
    }
  } catch (error) {
    console.warn('Failed to load search term from session storage:', error);
  }
  
  return '';
};
